import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { analysisKeys } from '../api/queryKeys';
import { useAnalysisProfileQuery } from '../api/useAnalysisProfileQuery';
import {
  hasProcessingExceededLimit,
  PROCESSING_POLL_LIMIT_MS,
  recordProcessingStart,
} from '../utils/processingLimit';

/**
 * Whether the analysis's current run has been watched past the poll limit, which
 * is when its polling stops, plus a manual re-check.
 *
 * Counts from when this client began watching the run (see `processingLimit`), and
 * flips on a timer rather than on a render, because nothing re-renders when the
 * limit passes while the profile is unchanged. The re-check re-reads the profile
 * and results once; a run that is still PROCESSING stays overdue.
 */
export const useProcessingOverdue = () => {
  const { projectId, analysisProfileId } = useAnalysisRouteParams();
  const queryClient = useQueryClient();
  const { data: profileEnvelope, refetch: refetchProfile } = useAnalysisProfileQuery({
    projectId,
    analysisProfileId,
  });
  const isProcessing = profileEnvelope?.data.latestStatus === 'PROCESSING';

  const [isOverdue, setIsOverdue] = useState(false);
  useEffect(() => {
    if (!isProcessing) {
      setIsOverdue(false);
      return undefined;
    }
    // Idempotent: keeps the start the profile query or the dispatch already recorded.
    recordProcessingStart(queryClient, analysisProfileId);
    if (hasProcessingExceededLimit(queryClient, analysisProfileId)) {
      setIsOverdue(true);
      return undefined;
    }
    setIsOverdue(false);
    const startedAtMs = Date.now() - elapsedMs(queryClient, analysisProfileId);
    const timer = setTimeout(
      () => setIsOverdue(true),
      Math.max(startedAtMs + PROCESSING_POLL_LIMIT_MS - Date.now(), 0)
    );
    return () => clearTimeout(timer);
  }, [isProcessing, analysisProfileId, queryClient]);

  const onCheckAgain = useCallback(() => {
    void refetchProfile();
    void queryClient.invalidateQueries({ queryKey: analysisKeys.results(analysisProfileId) });
  }, [refetchProfile, queryClient, analysisProfileId]);

  return { isOverdue: isProcessing && isOverdue, onCheckAgain };
};

// Time already spent watching the run, from the recorded start.
const elapsedMs = (queryClient: ReturnType<typeof useQueryClient>, analysisProfileId: string) => {
  const startedAtMs = queryClient.getQueryData<number>([
    ...analysisKeys.all,
    'processing-started-at',
    analysisProfileId,
  ]);
  return startedAtMs === undefined ? 0 : Date.now() - startedAtMs;
};
